import React, {createContext, useContext, useEffect, useRef} from 'react';
import type {OptionsType, TableRowType} from 'shared/types';
import {
    withDragTarget,
    DragProps,
} from 'shared/components/DragAndDrop/withDragTarget';
import {useCheckBrowsers} from 'shared/components/DragAndDrop/useCheckBrowsers';

interface RowProps extends DragProps {
    children: React.ReactNode;
    options: OptionsType;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    className: string;
}

interface RowContextType {
    previewReference?: (node: HTMLDivElement) => void;
    dragReference?: (node: HTMLDivElement) => void;
    showDragHandle?: boolean;
}

const RowContext = createContext<RowContextType>({});

export const useRowContext = () => useContext(RowContext);

const Row = ({
    children,
    options,
    onClick,
    className,
    isDragging,
    dragRef,
    previewRef,
}: RowProps) => {
    const {showDragHandle} = useCheckBrowsers();
    const rowReference = useRef();

    useEffect(() => {
        if (rowReference.current) {
            if (showDragHandle) {
                previewRef(rowReference.current);
            } else {
                dragRef(rowReference.current);
            }
        }
    }, [previewRef, dragRef, showDragHandle]);

    const opacity = isDragging ? 0.5 : 1;

    return (
        <RowContext.Provider
            value={{
                showDragHandle,
                dragReference: dragRef,
                previewReference: previewRef,
            }}>
            {typeof options.reorder === 'undefined' ? (
                <tr onClick={onClick} className={className}>
                    {children}
                </tr>
            ) : (
                <tr
                    ref={rowReference}
                    style={
                        showDragHandle
                            ? {opacity}
                            : {
                                  cursor: 'grab',
                                  opacity,
                              }
                    }
                    onClick={onClick}
                    className={className}>
                    {children}
                </tr>
            )}
        </RowContext.Provider>
    );
};

export const DndRow = withDragTarget<TableRowType, RowProps>(Row);

export default Row;
